import { rem } from '@mantine/core';
import { hmToMinutes, minutesToHm } from '@reditelji/shared';
import { useEffect, useState } from 'react';
import { INPUT } from '../volunteers/ui';

/** Arrow-key step, matching the 15-minute grid the schedule snaps to. */
const STEP_MIN = 15;
const MAX_MIN = 23 * 60 + 59;

/**
 * 24-hour time field.
 *
 * Deliberately not `<input type="time">`: that control renders in the browser's
 * UI language, so a coordinator whose Chrome is set to English sees "02:30 PM"
 * no matter what the page does — `lang`, `Accept-Language` and the document
 * locale all have no effect on it. A plain text input keeps every user on the
 * HH:MM the rest of the app uses.
 *
 * Typing is lenient ("930", "9.30", "9:3" all work); the value is normalised on
 * blur and Enter. Invalid input falls back to the last good value rather than
 * throwing, which the raw `hmToMinutes` call this replaced would do.
 */
export function TimeField({
  value,
  onChange,
  title,
  style,
}: {
  /** Minutes from midnight. */
  value: number;
  onChange: (minutes: number) => void;
  title?: string;
  style?: React.CSSProperties;
}) {
  const [text, setText] = useState(() => minutesToHm(value));

  // Follow external changes (undo, switching post/day) unless mid-edit.
  useEffect(() => {
    setText(minutesToHm(value));
  }, [value]);

  const commit = (raw: string) => {
    const min = parseTime(raw);
    if (min === null) {
      setText(minutesToHm(value));
      return;
    }
    setText(minutesToHm(min));
    if (min !== value) onChange(min);
  };

  const nudge = (delta: number) => {
    const base = parseTime(text) ?? value;
    const next = Math.min(MAX_MIN, Math.max(0, base + delta));
    setText(minutesToHm(next));
    if (next !== value) onChange(next);
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      maxLength={5}
      size={5}
      aria-label={title ?? 'Čas (24-urni zapis)'}
      title={title}
      placeholder="HH:MM"
      value={text}
      onChange={(e) => setText(e.currentTarget.value)}
      onBlur={(e) => commit(e.currentTarget.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit(e.currentTarget.value);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          nudge(STEP_MIN);
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          nudge(-STEP_MIN);
        }
      }}
      style={{
        ...INPUT,
        // 5 glyphs + horizontal padding + borders (box-sizing is border-box)
        width: 'calc(5ch + 20px)',
        fontFamily: 'var(--rr-font-mono)',
        fontSize: rem(13),
        padding: '6px 8px',
        ...style,
      }}
    />
  );
}

/**
 * "9:30" | "09:30" | "930" | "0930" | "9.30" | "9" → minutes, or null if the
 * text cannot be read as a time of day.
 */
export function parseTime(raw: string): number | null {
  const s = raw.trim();
  if (!s) return null;

  const digits = s.replace(/\D/g, '');
  let hm: string;
  if (/[:.\s]/.test(s)) {
    const m = /^(\d{1,2})\s*[:.\s]\s*(\d{1,2})$/.exec(s);
    if (!m) return null;
    hm = `${m[1]}:${m[2]!.padStart(2, '0')}`;
  } else if (digits.length <= 2) {
    hm = `${digits}:00`;
  } else if (digits.length === 3) {
    hm = `${digits.slice(0, 1)}:${digits.slice(1)}`;
  } else if (digits.length === 4) {
    hm = `${digits.slice(0, 2)}:${digits.slice(2)}`;
  } else {
    return null;
  }

  try {
    const min = hmToMinutes(hm);
    return min > MAX_MIN ? null : min;
  } catch {
    return null;
  }
}
